import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { defRoute, LoadedProps, RouteLoadProps } from '@components/router';
import { FixedContent, FixedPage } from '@course/components/fixed-page';
import { AdminNav, AdminTabWrapper } from '@course/components/admin-nav';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { IcoArrowLeft, IcoCheck, IcoExternalLink } from '@components/icons';
import { useEffect, useRef, useState } from 'preact/hooks';
import { BasicsStep } from './basics-step';
import { CheckoutStep } from './checkout-step';
import { ThankyouStep, resolveCtaUrl } from './thankyou-step';
import { UpsellStep, campaignCurrencies } from './upsell-step';
import { ReviewStep } from './review-step';
import { trimEmptyHtml } from '@components/minidoc/rich-html-field';
import { CheckoutSection, CampaignPageConfig, CampaignSection } from 'server/types/pmts-overrides';
import { migrateCampaignSections } from '@course/components/campaign/compat';
import { PriceRow } from 'server/types';
import * as pmtfmt from 'shared/payments/fmt';

type Campaign = RpxResponse<typeof rpx.campaigns.getCampaign>;

export function derivePriceBadgeText(price: PriceRow): string {
  if (price.payWhatYouCan) {
    return 'Pay What You Can';
  }
  if (price.priceInCents === 0 || price.paymentType === 'free') {
    return 'FREE';
  }
  const amount = pmtfmt.price({ priceInCents: price.priceInCents, currency: price.currency });
  if (price.paymentType === 'paymentplan' && price.numPayments && price.numPayments > 1) {
    return `${price.numPayments} payments of ${amount}`;
  }
  if (price.paymentType === 'subscription') {
    if (price.interval === 'month') {
      return `${amount}/month`;
    }
    if (price.interval === 'year') {
      return `${amount}/year`;
    }
    if (price.interval === 'week') {
      return `${amount}/week`;
    }
    return amount;
  }
  return `${amount} (One-Time Payment)`;
}

function withDerivedBadge(
  sections: CheckoutSection[],
  price: PriceRow | undefined,
): CheckoutSection[] {
  if (!price) {
    return sections;
  }
  let filled = false;
  return sections.map((section) => {
    if (filled || section.type !== 'hero' || section.content.priceBadgeText !== undefined) {
      return section;
    }
    filled = true;
    return {
      ...section,
      content: { ...section.content, priceBadgeText: derivePriceBadgeText(price) },
    };
  });
}

function prunePriceTags(
  map: Record<string, string[]>,
  activePriceIds: string[],
): Record<string, string[]> | undefined {
  const allowed = new Set(activePriceIds.filter(Boolean));
  const out: Record<string, string[]> = {};
  for (const [priceId, tags] of Object.entries(map)) {
    if (!allowed.has(priceId) || !tags.length) {
      continue;
    }
    out[priceId] = tags;
  }
  return Object.keys(out).length ? out : undefined;
}

function isoToLocalDatetime(iso?: string): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const STEPS = ['basics', 'checkout', 'thankyou', 'upsell', 'review'] as const;
type StepName = (typeof STEPS)[number];

const STEP_LABELS: Record<StepName, string> = {
  basics: 'Basics',
  checkout: 'Checkout Page',
  thankyou: 'Thank-You',
  upsell: 'Upsell',
  review: 'Review',
};

export const route = defRoute({
  load,
  Page,
  authLevel: 'guide',
  hideChat: true,
});

export type TenantCourse = RpxResponse<typeof rpx.campaigns.listTenantCourses>[number];

type LoadResult = {
  isNew: boolean;
  courses: TenantCourse[];
  campaign?: Campaign;
};

async function load(route: RouteLoadProps): Promise<LoadResult> {
  const courseId = route.params.courseId;
  const isNew = courseId === 'new';

  const courses = await rpx.campaigns.listTenantCourses();

  if (isNew) {
    return { isNew: true, courses };
  }

  const campaign = await rpx.campaigns.getCampaign({ courseId });
  return { isNew: false, courses, campaign };
}

export type WizardState = {
  courseId: string;
  courseTitle: string;
  slug: string;
  brand: string;
  useCustomCheckout: boolean;
  selectedPriceId: string;
  sections: CheckoutSection[];
  meta: { pageTitle?: string; description?: string };
  simpleCheckConsent: boolean;
  defaultPriceId?: string;
  prices: PriceRow[];
  welcomeEnabled: boolean;
  welcomeHeadline: string;
  welcomeSubtitle: string;
  welcomeBodyHtml: string;
  welcomeUpsellBodyHtml: string;
  welcomeCtaText: string;
  welcomeCtaUrl: string;
  welcomeVideoUrl: string;
  welcomeVideoCaption: string;
  welcomeCalendar?: {
    title: string;
    startDate: string;
    durationMinutes: number;
    description?: string;
    recurrence?: string;
    location?: string;
  };
  welcomeSmsOptin?: {
    heading?: string;
    description?: string;
    buttonText?: string;
    tag?: string;
  };
  // Absent means on, as it always was; { enabled: false } turns it off.
  welcomeSetPassword?: {
    enabled: boolean;
    position?: 'bottom' | 'afterBody';
    heading?: string;
    description?: string;
  };
  alternativePriceIds: string[];
  ontraportTags: string[];
  priceOntraportTags: Record<string, string[]>;
  freeTrialDays: number | undefined;
  deferredDate: string;
  upsellEnabled: boolean;
  // The upsell's offer price for each checkout currency, e.g. { USD, CAD }.
  upsellPriceIds: Record<string, string>;
  upsellSections: CheckoutSection[];
  upsellSectionsReturners: CheckoutSection[];
  upsellProductCourseId: string;
  upsellProductTitle: string;
  upsellOntraportTags: string[];
  upsellDeferredDate: string;
  upsellFreeTrialDays: number | undefined;
  redirectEnabled: boolean;
  redirectUrl: string;
  redirectAt: string;
};

function buildInitialState(data: LoadResult): WizardState {
  if (data.campaign) {
    const { course, prices } = data.campaign;
    const config = course.campaignPageConfig as CampaignPageConfig | undefined;
    const rawSections = config?.sections || [];
    const needsMigration = rawSections.length > 0 && !('content' in rawSections[0]);
    const sections = needsMigration
      ? migrateCampaignSections(rawSections as CampaignSection[])
      : (rawSections as CheckoutSection[]);
    const defaultPriceId = config?.defaultPriceId;
    const upsell = data.campaign.upsell;
    const welcome = data.campaign.welcome?.welcomePage;
    return {
      courseId: course.id,
      courseTitle: course.title,
      slug: course.campaignSlug || '',
      brand: config?.brand || 'mirasee',
      useCustomCheckout: course.useCustomCheckout || false,
      selectedPriceId: defaultPriceId || (prices[0]?.id ?? ''),
      sections,
      meta: config?.meta || {},
      simpleCheckConsent: !!config?.simpleCheckConsent,
      defaultPriceId,
      prices,
      welcomeEnabled: !!welcome,
      welcomeHeadline: welcome?.headline || '',
      welcomeSubtitle: welcome?.subtitle || '',
      welcomeBodyHtml: welcome?.bodyHtml || '',
      welcomeUpsellBodyHtml: welcome?.upsellBodyHtml || '',
      welcomeCtaText: welcome?.ctaText || '',
      welcomeCtaUrl: welcome?.ctaUrl || '',
      welcomeVideoUrl: welcome?.videoUrl || '',
      welcomeVideoCaption: welcome?.videoCaption || '',
      welcomeCalendar: welcome?.calendar,
      welcomeSmsOptin: welcome?.smsOptin,
      welcomeSetPassword: welcome?.setPassword,
      alternativePriceIds: config?.alternativePriceIds || [],
      ontraportTags: config?.ontraportTags || [],
      priceOntraportTags: config?.priceOntraportTags || {},
      freeTrialDays: config?.freeTrialDays ?? undefined,
      deferredDate: config?.deferredEnrollmentDate
        ? config.deferredEnrollmentDate.slice(0, 10)
        : '',
      upsellEnabled: !!upsell,
      upsellPriceIds: upsell?.priceIdsByCurrency || {},
      upsellSections: upsell?.pageConfig || defaultUpsellSections(),
      upsellSectionsReturners: upsell?.pageConfigReturners || [],
      upsellProductCourseId: upsell?.upsellCourseId || '',
      upsellProductTitle: upsell?.upsellCourseTitle || '',
      upsellOntraportTags: upsell?.ontraportTags || [],
      upsellDeferredDate: upsell?.deferredEnrollmentDate
        ? upsell.deferredEnrollmentDate.slice(0, 10)
        : '',
      upsellFreeTrialDays: upsell?.freeTrialDays ?? undefined,
      redirectEnabled: !!config?.redirect?.url,
      redirectUrl: config?.redirect?.url || '',
      redirectAt: isoToLocalDatetime(config?.redirect?.at),
    };
  }

  return {
    courseId: '',
    courseTitle: '',
    slug: '',
    brand: 'mirasee',
    useCustomCheckout: false,
    selectedPriceId: '',
    sections: defaultSections(),
    meta: {},
    simpleCheckConsent: false,
    prices: [],
    welcomeEnabled: true,
    welcomeHeadline: '',
    welcomeSubtitle: '',
    welcomeBodyHtml: '',
    welcomeUpsellBodyHtml: '',
    welcomeCtaText: '',
    welcomeCtaUrl: '',
    welcomeVideoUrl: '',
    welcomeVideoCaption: '',
    welcomeCalendar: undefined,
    welcomeSmsOptin: undefined,
    welcomeSetPassword: undefined,
    alternativePriceIds: [],
    ontraportTags: [],
    priceOntraportTags: {},
    freeTrialDays: undefined,
    deferredDate: '',
    upsellEnabled: false,
    upsellPriceIds: {},
    upsellSections: defaultUpsellSections(),
    upsellSectionsReturners: [],
    upsellProductCourseId: '',
    upsellProductTitle: '',
    upsellOntraportTags: [],
    upsellDeferredDate: '',
    upsellFreeTrialDays: undefined,
    redirectEnabled: false,
    redirectUrl: '',
    redirectAt: '',
  };
}

function defaultSections(): CheckoutSection[] {
  return [
    {
      type: 'hero',
      settings: { gradientFrom: '#0089bd', gradientTo: '#013ea6' },
      content: { headline: '', subtitle: '' },
    },
    {
      type: 'content',
      settings: { maxWidth: 'wide' },
      content: { blocks: [{ type: 'paragraph', text: '' }] },
    },
    {
      type: 'integratedCheckout',
      content: {},
    },
    {
      type: 'faq',
      content: { heading: 'Frequently Asked Questions', items: [] },
    },
    {
      type: 'footer',
      content: {},
    },
  ];
}

function defaultUpsellSections(): CheckoutSection[] {
  return [
    {
      type: 'hero',
      settings: { gradientFrom: '#0089bd', gradientTo: '#013ea6' },
      content: { headline: 'Special Offer' },
    },
    {
      type: 'content',
      settings: { maxWidth: 'wide' },
      content: { blocks: [{ type: 'paragraph', text: '' }] },
    },
    {
      type: 'valueStack',
      content: { heading: "Here's what you get", items: [{ bold: '', text: '' }] },
    },
    {
      type: 'cta',
      content: { acceptText: 'Yes, add this!', declineText: 'No thanks, continue' },
    },
    {
      type: 'footer',
      content: {},
    },
  ];
}

function StepIndicator({
  currentStep,
  onStepClick,
}: {
  currentStep: StepName;
  onStepClick: (step: StepName) => void;
}) {
  const currentIndex = STEPS.indexOf(currentStep);

  return (
    <div class="flex items-center gap-0 mb-8">
      {STEPS.map((step, i) => {
        const isDone = i < currentIndex;
        const isActive = step === currentStep;

        return (
          <span key={step} class="flex items-center flex-1 last:flex-none">
            <button
              type="button"
              onClick={() => onStepClick(step)}
              class={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${
                isActive
                  ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200'
                  : isDone
                    ? 'text-green-700 hover:bg-green-50'
                    : 'text-gray-400 hover:bg-gray-50'
              }`}
            >
              <span
                class={`size-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  isActive
                    ? 'bg-indigo-600 text-white'
                    : isDone
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {isDone ? <IcoCheck class="size-3.5" /> : i + 1}
              </span>
              {STEP_LABELS[step]}
            </button>
            {i < STEPS.length - 1 && (
              <div
                class={`flex-1 h-0.5 mx-2 rounded-full transition-colors ${
                  isDone ? 'bg-green-300' : 'bg-gray-200'
                }`}
              />
            )}
          </span>
        );
      })}
    </div>
  );
}

function getStorageKey(courseId: string) {
  return `campaign-wizard:${courseId || 'new'}`;
}

function loadDraft(courseId: string): { state?: WizardState; step?: StepName } | undefined {
  try {
    const raw = sessionStorage.getItem(getStorageKey(courseId));
    if (!raw) {
      return undefined;
    }
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function saveDraft(courseId: string, state: WizardState, step: StepName) {
  try {
    sessionStorage.setItem(getStorageKey(courseId), JSON.stringify({ state, step }));
  } catch {
    // sessionStorage may be unavailable
  }
}

function clearDraft(courseId: string) {
  try {
    sessionStorage.removeItem(getStorageKey(courseId));
  } catch {
    // sessionStorage may be unavailable
  }
}

function Page(props: LoadedProps<typeof load>) {
  const data = props.state;
  const routeCourseId = props.state.isNew ? '' : props.state.campaign?.course.id || '';
  const draft = useRef(routeCourseId ? loadDraft(routeCourseId) : undefined).current;

  const [step, setStep] = useState<StepName>(() => draft?.step || 'basics');
  const [state, setState] = useState<WizardState>(() => {
    const serverState = buildInitialState(data);
    if (draft?.state) {
      return {
        ...serverState,
        ...draft.state,
        prices: serverState.prices.length ? serverState.prices : draft.state.prices,
      };
    }
    return serverState;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAs, setSavedAs] = useState<'published' | undefined>();
  const [savedAt, setSavedAt] = useState<Date | undefined>();
  const [, forceTick] = useState(0);
  const lastSavedStateRef = useRef<string>('');

  useEffect(() => {
    lastSavedStateRef.current = JSON.stringify(buildInitialState(data));
  }, []);

  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (routeCourseId) {
      saveDraft(routeCourseId, state, step);
    }
  }, [state, step, routeCourseId]);

  function updateState(partial: Partial<WizardState>) {
    setState((prev) => ({ ...prev, ...partial }));
  }

  async function handleSave(publish: boolean) {
    if (!state.courseId || !state.slug) {
      setError('Please select a course and set a URL slug.');
      return;
    }

    if (state.welcomeEnabled && !state.selectedPriceId) {
      setError('Please select a price before enabling the thank-you page.');
      return;
    }

    if (state.redirectEnabled && (!state.redirectUrl || !state.redirectAt)) {
      setError('Please set both a redirect URL and date/time, or disable the redirect.');
      return;
    }

    if (state.redirectEnabled && state.redirectUrl && !/^https?:\/\//.test(state.redirectUrl)) {
      setError('Redirect URL must start with https:// or http://.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const redirect =
        state.redirectEnabled && state.redirectUrl && state.redirectAt
          ? { url: state.redirectUrl, at: new Date(state.redirectAt).toISOString() }
          : undefined;

      const selectedPrice = state.prices.find((p) => p.id === state.selectedPriceId);
      const sectionsWithBadge = withDerivedBadge(state.sections, selectedPrice);

      const config: CampaignPageConfig = {
        sections: sectionsWithBadge,
        defaultPriceId: state.selectedPriceId || undefined,
        alternativePriceIds: state.alternativePriceIds.length
          ? state.alternativePriceIds
          : undefined,
        brand: state.brand !== 'mirasee' ? state.brand : undefined,
        meta: state.meta,
        simpleCheckConsent: state.simpleCheckConsent || undefined,
        ontraportTags: state.ontraportTags.length ? state.ontraportTags : undefined,
        priceOntraportTags: prunePriceTags(state.priceOntraportTags, [
          state.selectedPriceId,
          ...state.alternativePriceIds,
        ]),
        freeTrialDays: state.freeTrialDays,
        deferredEnrollmentDate: state.deferredDate || undefined,
        redirect,
      };

      await rpx.campaigns.saveCampaign({
        courseId: state.courseId,
        slug: state.slug,
        config,
        defaultPriceId: state.selectedPriceId || undefined,
        useCustomCheckout: publish,
      });

      const ctaSection = state.upsellSections.find((s) => s.type === 'cta');
      await rpx.campaigns.saveCampaignUpsell({
        courseId: state.courseId,
        selectedPriceId: state.selectedPriceId || undefined,
        enabled: state.upsellEnabled,
        upsellProductCourseId: state.upsellProductCourseId || undefined,
        upsellPriceIds: campaignCurrencies(state)
          .map((currency) => state.upsellPriceIds[currency])
          .filter(Boolean),
        pageConfig: state.upsellEnabled ? state.upsellSections : undefined,
        pageConfigReturners:
          state.upsellEnabled && state.upsellSectionsReturners.length
            ? state.upsellSectionsReturners
            : undefined,
        acceptText: ctaSection?.type === 'cta' ? ctaSection.content.acceptText : undefined,
        declineText: ctaSection?.type === 'cta' ? ctaSection.content.declineText : undefined,
        ontraportTags: state.upsellOntraportTags.length ? state.upsellOntraportTags : undefined,
        deferredEnrollmentDate: state.upsellDeferredDate || undefined,
        freeTrialDays: state.upsellFreeTrialDays,
      });

      await rpx.campaigns.saveCampaignWelcomeDefault({
        courseId: state.courseId,
        enabled: state.welcomeEnabled,
        headline: state.welcomeHeadline || undefined,
        subtitle: state.welcomeSubtitle || undefined,
        bodyHtml: trimEmptyHtml(state.welcomeBodyHtml),
        upsellBodyHtml: trimEmptyHtml(state.welcomeUpsellBodyHtml),
        ctaText: state.welcomeCtaText || undefined,
        ctaUrl: resolveCtaUrl(state) || undefined,
        videoUrl: state.welcomeVideoUrl || undefined,
        videoCaption: state.welcomeVideoCaption || undefined,
        calendar: state.welcomeCalendar,
        smsOptin: state.welcomeSmsOptin,
        setPassword: state.welcomeSetPassword,
      });

      updateState({ useCustomCheckout: publish });
      if (routeCourseId) {
        clearDraft(routeCourseId);
      }
      lastSavedStateRef.current = JSON.stringify({ ...state, useCustomCheckout: publish });
      setSavedAt(new Date());
      if (publish) {
        setSavedAs('published');
      }

      if (publish && state.slug) {
        const liveUrl = `${window.location.origin}/c/${state.slug}`;
        navigator.clipboard.writeText(liveUrl).catch(() => {});
        setStep('review');
      }

      if (data.isNew) {
        await props.router.redirectTo(`/admin/campaigns/${state.courseId}`);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save campaign.');
    } finally {
      setSaving(false);
    }
  }

  const currentIndex = STEPS.indexOf(step);
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === STEPS.length - 1;

  const currentStateJson = JSON.stringify(state);
  const hasUnsavedChanges =
    lastSavedStateRef.current !== '' && lastSavedStateRef.current !== currentStateJson;

  let saveIndicator: { text: string; dotClass: string; textClass: string } | undefined;
  if (saving) {
    saveIndicator = {
      text: 'Saving…',
      dotClass: 'bg-gray-400 animate-pulse',
      textClass: 'text-gray-500',
    };
  } else if (hasUnsavedChanges) {
    saveIndicator = {
      text: 'Unsaved changes',
      dotClass: 'bg-amber-500',
      textClass: 'text-amber-600',
    };
  } else if (savedAt) {
    const secs = Math.floor((Date.now() - savedAt.getTime()) / 1000);
    let rel: string;
    if (secs < 10) {
      rel = 'just now';
    } else if (secs < 60) {
      rel = `${secs}s ago`;
    } else if (secs < 3600) {
      rel = `${Math.floor(secs / 60)}m ago`;
    } else {
      rel = savedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    saveIndicator = {
      text: `Saved ${rel}`,
      dotClass: 'bg-green-500',
      textClass: 'text-gray-500',
    };
  }

  return (
    <FixedPage title={data.isNew ? 'New Campaign' : 'Edit Campaign'}>
      <FixedContent class="bg-white">
        <AdminNav
          currentPage="campaigns"
          pageTitle={data.isNew ? 'New Campaign' : 'Edit Campaign'}
        />
        <AdminTabWrapper>
          <div class="flex items-center gap-3 mb-4">
            <a
              href="/admin/campaigns"
              class="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <IcoArrowLeft class="size-4" />
              Campaigns
            </a>
            {!data.isNew && (
              <>
                <span class="text-gray-300">·</span>
                <a
                  href={`/admin/campaigns/${state.courseId}/funnel`}
                  class="text-sm text-gray-500 hover:text-gray-700"
                >
                  View funnel →
                </a>
              </>
            )}
          </div>

          <StepIndicator currentStep={step} onStepClick={setStep} />

          {error && <div class="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

          <div class="bg-white rounded-xl border border-gray-200 p-6 min-h-96">
            {step === 'basics' && (
              <BasicsStep state={state} courses={data.courses} updateState={updateState} />
            )}
            {step === 'checkout' && <CheckoutStep state={state} updateState={updateState} />}
            {step === 'thankyou' && <ThankyouStep state={state} updateState={updateState} />}
            {step === 'upsell' && (
              <UpsellStep state={state} courses={data.courses} updateState={updateState} />
            )}
            {step === 'review' && <ReviewStep state={state} />}
          </div>

          {savedAs === 'published' && (
            <div
              class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setSavedAs(undefined);
                }
              }}
            >
              <div class="bg-white rounded-2xl shadow-xl max-w-sm w-full mx-4 py-6 px-6">
                <div class="flex items-center gap-3 mb-4">
                  <div class="size-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <IcoCheck class="size-5 text-green-600" />
                  </div>
                  <div>
                    <h3 class="text-base font-bold text-gray-900">Campaign Published</h3>
                    <p class="text-xs text-gray-500">URL copied to clipboard</p>
                  </div>
                </div>
                <a
                  href={`/c/${state.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  class="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors mb-4"
                >
                  <span class="truncate">
                    {window.location.host}/c/{state.slug}
                  </span>
                  <IcoExternalLink class="size-3.5 text-gray-400 flex-shrink-0" />
                </a>
                <p class="text-xs text-gray-500 mb-5">
                  Preview the page, test a checkout, then share the link.
                </p>
                <div class="flex gap-3">
                  <BtnSecondary class="flex-1" onClick={() => setSavedAs(undefined)}>
                    Continue Editing
                  </BtnSecondary>
                  <BtnPrimary class="flex-1" href="/admin/campaigns">
                    Done
                  </BtnPrimary>
                </div>
              </div>
            </div>
          )}

          <div class="sticky bottom-0 bg-white/95 backdrop-blur-sm shadow-[0_-4px_20px_-4px_rgba(15,23,42,0.08)] -mx-8 px-8 py-5 mt-8 flex items-center justify-between z-10">
            <div class="flex items-center">
              {!isFirst && (
                <button
                  type="button"
                  onClick={() => setStep(STEPS[currentIndex - 1])}
                  class="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <IcoArrowLeft class="size-4" />
                  Back
                </button>
              )}
            </div>
            <div class="flex items-center gap-5">
              {saveIndicator && (
                <span
                  class={`flex items-center gap-2 text-xs font-medium ${saveIndicator.textClass}`}
                >
                  <span class={`size-1.5 rounded-full ${saveIndicator.dotClass}`} />
                  {saveIndicator.text}
                </span>
              )}
              <div class="flex gap-3">
                <BtnSecondary onClick={() => handleSave(false)} isLoading={saving}>
                  Save Draft
                </BtnSecondary>
                {!isLast && (
                  <BtnPrimary onClick={() => setStep(STEPS[currentIndex + 1])}>Next</BtnPrimary>
                )}
                {isLast && (
                  <BtnPrimary onClick={() => handleSave(true)} isLoading={saving}>
                    {state.useCustomCheckout ? 'Save & Publish' : 'Publish'}
                  </BtnPrimary>
                )}
              </div>
            </div>
          </div>
        </AdminTabWrapper>
      </FixedContent>
    </FixedPage>
  );
}
